import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { LexAIService } from '../services/api';
import { KanbanDeal, KanbanDealsResponse, DEAL_STATUSES } from '../types/api';
import KanbanColumn from './KanbanColumn';
import { useGlobalCanvas } from '../contexts/GlobalCanvasContext';

const DealKanbanBoard: React.FC = () => {
  const [data, setData] = useState<KanbanDealsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [visibleStatuses, setVisibleStatuses] = useState<Set<string>>(new Set(DEAL_STATUSES));
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [syncing, setSyncing] = useState(false);

  const { openOpportunity } = useGlobalCanvas();

  // Initial data fetch
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await LexAIService.getKanbanDeals();
        if (!cancelled) {
          setData(result);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load deals');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, []);

  const handleRefresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const freshData = await LexAIService.getKanbanDeals(undefined, true);
      setData(freshData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSyncAll = useCallback(async () => {
    setSyncing(true);
    try {
      await LexAIService.syncAllDeals(true);
      await handleRefresh();
    } finally {
      setSyncing(false);
    }
  }, [handleRefresh]);

  // Listen for sync events
  useEffect(() => {
    const handler = () => { handleRefresh(); };
    window.addEventListener('lexai-sync-complete', handler);
    return () => window.removeEventListener('lexai-sync-complete', handler);
  }, [handleRefresh]);

  // Group deals by status
  const dealsByStatus = useMemo(() => {
    if (!data?.deals) return new Map<string, KanbanDeal[]>();
    const filtered = searchQuery
      ? data.deals.filter(d =>
          d.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          d.clientFolder.toLowerCase().includes(searchQuery.toLowerCase()) ||
          d.applicants.some(a => a.personName.toLowerCase().includes(searchQuery.toLowerCase()))
        )
      : data.deals;

    const map = new Map<string, KanbanDeal[]>();
    DEAL_STATUSES.forEach(s => map.set(s, []));
    map.set('Other', []);
    filtered.forEach(deal => {
      const status = (DEAL_STATUSES as readonly string[]).includes(deal.status) ? deal.status : 'Other';
      const arr = map.get(status) || [];
      arr.push(deal);
      map.set(status, arr);
    });
    return map;
  }, [data, searchQuery]);

  const handleToggleStatus = useCallback((status: string) => {
    setVisibleStatuses(prev => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  }, []);

  const handleToggleAll = useCallback(() => {
    setVisibleStatuses(prev =>
      prev.size === DEAL_STATUSES.length ? new Set() : new Set(DEAL_STATUSES)
    );
  }, []);

  const handleToggleCardExpand = useCallback((dealId: string) => {
    setExpandedCards(prev => {
      const next = new Set(prev);
      if (next.has(dealId)) next.delete(dealId);
      else next.add(dealId);
      return next;
    });
  }, []);

  const handleCardClick = useCallback((deal: KanbanDeal) => {
    openOpportunity?.({
      id: deal.mercuryId || deal.id,
      name: deal.displayName,
      clientFolder: deal.clientFolder,
      dealFolder: deal.dealFolder,
      status: deal.status,
      amount: deal.amount,
    });
  }, [openOpportunity]);

  const handleLaunchAssistant = useCallback((deal: KanbanDeal) => {
    openOpportunity?.({
      id: deal.mercuryId || deal.id,
      name: deal.displayName,
      clientFolder: deal.clientFolder,
      dealFolder: deal.dealFolder,
      status: deal.status,
      amount: deal.amount,
    });
  }, [openOpportunity]);

  // Handle drag-and-drop status change
  const handleDealDrop = useCallback(async (deal: KanbanDeal, newStatus: string) => {
    if (!deal.dealFolder) return; // Can't update status for leads without deal folder

    const oldStatus = deal.status;

    // Optimistic update - move card immediately in local state
    setData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        deals: prev.deals.map(d =>
          d.id === deal.id ? { ...d, status: newStatus } : d
        ),
      };
    });

    try {
      await LexAIService.updateDealStatus(deal.clientFolder, deal.dealFolder, newStatus);
    } catch (err) {
      console.error('Failed to update deal status:', err);
      // Revert on failure
      setData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          deals: prev.deals.map(d =>
            d.id === deal.id ? { ...d, status: oldStatus } : d
          ),
        };
      });
    }
  }, []);

  // Loading state
  if (loading && !data) {
    return (
      <div style={{ padding: 24 }}>
        {/* Header skeleton */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 28, height: 28, borderRadius: 6, background: 'rgba(255,255,255,0.05)' }} />
            <div style={{ width: 160, height: 24, borderRadius: 6, background: 'rgba(255,255,255,0.05)' }} />
          </div>
        </div>
        {/* Column skeletons */}
        <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 24 }}>
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} style={{
              width: 310,
              flexShrink: 0,
              height: 400,
              borderRadius: 8,
              background: 'rgba(255,255,255,0.03)',
              animation: 'pulse 1.5s ease-in-out infinite',
            }}>
              <div style={{ padding: 14 }}>
                <div style={{ width: '60%', height: 16, borderRadius: 4, background: 'rgba(255,255,255,0.05)', marginBottom: 8 }} />
                <div style={{ width: '30%', height: 12, borderRadius: 4, background: 'rgba(255,255,255,0.03)' }} />
              </div>
            </div>
          ))}
        </div>
        <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>
      </div>
    );
  }

  // Empty state (no deals at all)
  if (!loading && (!data?.deals || data.deals.length === 0) && !error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 24px',
        color: 'var(--obsidian-text-secondary)'
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: 64, opacity: 0.3, marginBottom: 16 }}>
          view_kanban
        </span>
        <h3 style={{ margin: '0 0 8px', fontSize: 20, color: 'var(--obsidian-text-primary)' }}>No deals found</h3>
        <p style={{ margin: '0 0 24px', fontSize: 14 }}>Sync your deals from Mercury CRM to get started</p>
        <button
          onClick={handleSyncAll}
          disabled={syncing}
          style={{
            padding: '10px 24px',
            fontSize: 14,
            fontWeight: 600,
            color: '#fff',
            background: 'var(--obsidian-accent)',
            border: 'none',
            borderRadius: 8,
            cursor: syncing ? 'not-allowed' : 'pointer',
            opacity: syncing ? 0.7 : 1,
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>sync</span>
          {syncing ? 'Syncing...' : 'Sync All Deals'}
        </button>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 24px',
        color: 'var(--obsidian-text-secondary)'
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: 48, color: '#f44336', marginBottom: 16 }}>error</span>
        <h3 style={{ margin: '0 0 8px', color: 'var(--obsidian-text-primary)' }}>Failed to load deals</h3>
        <p style={{ margin: '0 0 16px', fontSize: 13 }}>{error}</p>
        <button
          onClick={handleRefresh}
          style={{
            padding: '8px 20px',
            fontSize: 13,
            color: 'var(--obsidian-accent)',
            background: 'transparent',
            border: '1px solid var(--obsidian-accent)',
            borderRadius: 6,
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  const allStatuses = [...DEAL_STATUSES, ...((dealsByStatus.has('Other') && (dealsByStatus.get('Other')?.length || 0) > 0) ? ['Other'] : [])];

  return (
    <DndProvider backend={HTML5Backend}>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        {/* Header Bar */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 24px 12px',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 24, color: 'var(--obsidian-accent)' }}>
              view_kanban
            </span>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--obsidian-text-primary)' }}>
              Deal Pipeline
            </h2>
            {loading && (
              <span className="material-symbols-outlined" style={{
                fontSize: 18,
                color: 'var(--obsidian-text-secondary)',
                animation: 'spin 1s linear infinite'
              }}>sync</span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* Search input */}
            <div style={{ position: 'relative' }}>
              <span className="material-symbols-outlined" style={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: 16,
                color: 'var(--obsidian-text-secondary)',
                pointerEvents: 'none'
              }}>search</span>
              <input
                type="text"
                placeholder="Search deals..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{
                  padding: '6px 10px 6px 30px',
                  fontSize: 13,
                  color: 'var(--obsidian-text-primary)',
                  background: 'var(--obsidian-sidebar-bg)',
                  border: '1px solid var(--obsidian-border)',
                  borderRadius: 6,
                  outline: 'none',
                  width: 180
                }}
              />
            </div>
            {/* Refresh button */}
            <button
              onClick={handleRefresh}
              disabled={loading}
              title="Refresh"
              style={{
                padding: '6px 8px',
                background: 'transparent',
                border: '1px solid var(--obsidian-border)',
                borderRadius: 6,
                cursor: loading ? 'not-allowed' : 'pointer',
                color: 'var(--obsidian-text-secondary)',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>refresh</span>
            </button>
            {/* Sync All button */}
            <button
              onClick={handleSyncAll}
              disabled={syncing}
              style={{
                padding: '6px 14px',
                fontSize: 13,
                fontWeight: 500,
                color: '#fff',
                background: 'var(--obsidian-accent)',
                border: 'none',
                borderRadius: 6,
                cursor: syncing ? 'not-allowed' : 'pointer',
                opacity: syncing ? 0.7 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
            >
              <span className="material-symbols-outlined" style={{
                fontSize: 16,
                animation: syncing ? 'spin 1s linear infinite' : 'none'
              }}>sync</span>
              {syncing ? 'Syncing...' : 'Sync All Deals'}
            </button>
          </div>
        </div>

        {/* Filter Bar */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          padding: '0 24px 12px',
          alignItems: 'center',
          flexShrink: 0
        }}>
          <span style={{ fontSize: 12, color: 'var(--obsidian-text-secondary)', marginRight: 4 }}>
            Filter Columns
          </span>
          {/* View All chip */}
          <button
            onClick={handleToggleAll}
            style={{
              padding: '3px 10px',
              fontSize: 11,
              fontWeight: 500,
              borderRadius: 12,
              cursor: 'pointer',
              border: visibleStatuses.size === DEAL_STATUSES.length
                ? '1px solid var(--obsidian-accent)'
                : '1px solid var(--obsidian-border)',
              background: visibleStatuses.size === DEAL_STATUSES.length
                ? 'var(--obsidian-accent)'
                : 'transparent',
              color: visibleStatuses.size === DEAL_STATUSES.length
                ? '#fff'
                : 'var(--obsidian-text-secondary)'
            }}
          >
            View All
          </button>
          {/* Status chips */}
          {allStatuses.map(status => {
            const count = dealsByStatus.get(status)?.length || 0;
            const isActive = visibleStatuses.has(status);
            return (
              <button
                key={status}
                onClick={() => handleToggleStatus(status)}
                style={{
                  padding: '3px 10px',
                  fontSize: 11,
                  fontWeight: 500,
                  borderRadius: 12,
                  cursor: 'pointer',
                  border: isActive
                    ? '1px solid var(--obsidian-accent)'
                    : '1px solid var(--obsidian-border)',
                  background: isActive ? 'var(--obsidian-accent)' : 'transparent',
                  color: isActive ? '#fff' : 'var(--obsidian-text-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4
                }}
              >
                {status}
                {count > 0 && (
                  <span style={{
                    fontSize: 10,
                    fontWeight: 700,
                    background: isActive ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.08)',
                    borderRadius: 8,
                    padding: '0 5px',
                    lineHeight: '16px'
                  }}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Columns Container */}
        <div style={{
          display: 'flex',
          gap: 12,
          overflowX: 'auto',
          padding: '0 24px 24px',
          flex: 1,
          minHeight: 0
        }}>
          {allStatuses
            .filter(status => visibleStatuses.has(status))
            .map(status => {
              const deals = dealsByStatus.get(status) || [];
              const totalAmount = deals.reduce((sum, d) => sum + (d.amount || 0), 0);
              return (
                <KanbanColumn
                  key={status}
                  status={status}
                  deals={deals}
                  totalAmount={totalAmount}
                  expandedCards={expandedCards}
                  onToggleCardExpand={handleToggleCardExpand}
                  onLaunchAssistant={handleLaunchAssistant}
                  onCardClick={handleCardClick}
                  onDealDrop={handleDealDrop}
                />
              );
            })}
        </div>

        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    </DndProvider>
  );
};

export default DealKanbanBoard;
